import {
    RemoServiceID,
    RemoServices,
    RemoServicesCatalog,
    RemoServicesInstance,
} from "@obsbot/remo-services";
import React, { ComponentType, createContext, FC, useContext, useState } from "react";
import { useSafePromise } from "remo-components";
import { useIsomorphicLayoutEffect } from "react-use";

export const RemoServicesContext = createContext<RemoServices>(null as unknown as RemoServices);

export const RemoServicesContextProvider: FC<{ children: React.ReactNode }> = props => (
    <RemoServicesContext.Provider value={RemoServices.getInstance()}>
        {props.children}
    </RemoServicesContext.Provider>
);

export const useRemoService = <T extends RemoServiceID>(
    name: T,
): RemoServicesInstance<T> | undefined => {
    const remoServices = useContext(RemoServicesContext);
    const [service, setService] = useState<RemoServicesInstance<T> | null>();
    const sp = useSafePromise();
    useIsomorphicLayoutEffect(() => {
        sp(remoServices.requestService(name)).then(setService);
    }, [remoServices, name]);
    if (service === null) {
        throw new Error(`Remo Service ${name} is not registered`);
    }
    return service;
};

export type WithRemoServicesProps<P = {}, S extends RemoServiceID = RemoServiceID> = P &
    Pick<RemoServicesCatalog, S>;

export const withRemoServices = <S extends RemoServiceID>(...names: S[]) => {
    return <P extends {}>(Component: ComponentType<WithRemoServicesProps<P, S>>) => {
        const WithRemoServices: FC<P> = props => {
            const remoServices = useContext(RemoServicesContext);
            const [services, setServices] = useState<Pick<RemoServicesCatalog, S>>();
            const sp = useSafePromise();
            useIsomorphicLayoutEffect(() => {
                sp(Promise.all(names.map(name => remoServices.requestService(name)))).then(
                    serviceList =>
                        setServices(
                            serviceList.reduce((acc, service, i) => {
                                acc[names[i]] = service;
                                return acc;
                            }, {} as any),
                        ),
                );
            }, [remoServices]);
            return services ? <Component {...services} {...props} /> : null;
        };
        return WithRemoServices;
    };
};
